import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

/**
 * RealtimeContext — single shared WebSocket connection per tab.
 * Components subscribe via useRealtime(callback).
 *
 * Auto-reconnects with exponential backoff (max 30s).
 * Pings every 30s to keep the connection alive.
 */
const RealtimeContext = createContext({ subscribe: () => () => {}, connected: false });

export const RealtimeProvider = ({ children, token }) => {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const subscribersRef = useRef(new Set());
  const mountedRef = useRef(true);
  const retryTimerRef = useRef(null);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    const currentToken = tokenRef.current;
    if (!currentToken) return;
    if (wsRef.current && [WebSocket.CONNECTING, WebSocket.OPEN].includes(wsRef.current.readyState)) return;
    const httpUrl = process.env.REACT_APP_BACKEND_URL;
    const wsUrl = httpUrl.replace(/^http/, "ws") + `/api/ws?token=${encodeURIComponent(currentToken)}`;
    let ws;
    try { ws = new WebSocket(wsUrl); } catch { return; }
    wsRef.current = ws;
    ws.onopen = () => {
      if (currentToken !== tokenRef.current || !mountedRef.current) { ws.close(); return; }
      setConnected(true);
      retryRef.current = 0;
    };
    ws.onmessage = (e) => {
      if (e.data === "pong") return;
      try {
        const payload = JSON.parse(e.data);
        subscribersRef.current.forEach((cb) => {
          try { cb(payload); } catch (e) { /* silently ignore non-critical fetch errors */ console.debug(e); }
        });
      } catch { /* malformed */ }
    };
    ws.onclose = () => {
      if (wsRef.current !== ws) return;
      wsRef.current = null;
      setConnected(false);
      if (!mountedRef.current) return;
      // Cap backoff at 30s; give up forever after 10 attempts to avoid noise
      if (retryRef.current >= 10) return;
      const delay = Math.min(30000, 1000 * Math.pow(2, retryRef.current));
      retryRef.current += 1;
      retryTimerRef.current = setTimeout(connect, delay);
    };
    ws.onerror = () => { try { ws.close(); } catch (e) { /* silently ignore non-critical fetch errors */ console.debug(e); } };
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    retryRef.current = 0;
    setConnected(false);
    // Rinvia l'handshake: il primo ciclo di StrictMode può essere annullato senza aprire un socket.
    retryTimerRef.current = setTimeout(connect, 0);
    const ping = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        try { wsRef.current.send("ping"); } catch (e) { /* silently ignore non-critical fetch errors */ console.debug(e); }
      }
    }, 30000);
    return () => {
      mountedRef.current = false;
      clearTimeout(retryTimerRef.current);
      clearInterval(ping);
      const previous = wsRef.current;
      wsRef.current = null;
      if (previous) {
        previous.onclose = null;
        previous.onerror = null;
        if (previous.readyState === WebSocket.CONNECTING) previous.onopen = () => previous.close();
        else previous.close();
      }
    };
  }, [connect, token]);

  const subscribe = useCallback((cb) => {
    subscribersRef.current.add(cb);
    return () => subscribersRef.current.delete(cb);
  }, []);

  return (
    <RealtimeContext.Provider value={{ subscribe, connected }}>
      {children}
    </RealtimeContext.Provider>
  );
};

/**
 * Hook to subscribe to realtime events.
 * Returns { connected }.
 */
export const useRealtime = (onEvent) => {
  const { subscribe, connected } = useContext(RealtimeContext);
  const cbRef = useRef(onEvent);
  useEffect(() => { cbRef.current = onEvent; }, [onEvent]);
  useEffect(() => {
    if (!subscribe) return;
    const stable = (evt) => { if (cbRef.current) cbRef.current(evt); };
    return subscribe(stable);
  }, [subscribe]);
  return { connected };
};

export default useRealtime;
